/**
 * `fetch` com nova tentativa (backoff exponencial), tempo-limite e
 * cancelamento via AbortSignal.
 */

export class HttpError extends Error {
  readonly status: number;
  /** Espera pedida pelo servidor (`Retry-After`), em ms, se houver. */
  readonly retryAfterMs: number | null;
  constructor(status: number, url: string, retryAfterMs: number | null = null) {
    super(`HTTP ${status} em ${url}`);
    this.name = 'HttpError';
    this.status = status;
    this.retryAfterMs = retryAfterMs;
  }
}

/** `Retry-After` em ms (segundos ou data HTTP), ou null. */
export function parseRetryAfter(value: string | null, now = Date.now()): number | null {
  if (!value) return null;
  const s = Number(value);
  if (Number.isFinite(s)) return s >= 0 ? s * 1000 : null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? Math.max(0, t - now) : null;
}

export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

export function abortError(): DOMException {
  return new DOMException('Operação cancelada', 'AbortError');
}

/** Espera `ms`, rejeitando com AbortError se o sinal for acionado. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const id = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(id);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export class TimeoutError extends Error {
  constructor(url: string, ms: number) {
    super(`Tempo esgotado (${Math.round(ms / 1000)} s) em ${url}`);
    this.name = 'TimeoutError';
  }
}

export interface RetryOptions {
  /** Número de novas tentativas após a primeira (padrão 2). */
  retries?: number;
  /** Atraso da primeira nova tentativa (ms); dobra a cada tentativa (padrão 1000). */
  baseDelayMs?: number;
  /** Tempo-limite de cada tentativa (ms, padrão 30 s) — cobre cabeçalhos E corpo. */
  timeoutMs?: number;
  /** Tempo-limite por tentativa (1ª, 2ª, …); a última vale para as seguintes. Tem prioridade sobre `timeoutMs`. */
  timeouts?: readonly number[];
  signal?: AbortSignal;
}

/** Erros que valem nova tentativa: rede, tempo-limite, 5xx e 429. */
function isRetryable(e: unknown): boolean {
  if (e instanceof HttpError) return e.status >= 500 || e.status === 429 || e.status === 408;
  return !isAbortError(e);
}

/**
 * Faz o fetch e LÊ O CORPO com `read` (ex.: `r => r.text()`, `r => r.blob()`)
 * dentro do mesmo cancelamento e tempo-limite: um corpo travado ou cancelado
 * não fica pendurado. Status fora de 2xx vira `HttpError`; tempo esgotado vira
 * `TimeoutError`. O cancelamento pelo `signal` externo (AbortError) nunca é repetido.
 */
export async function fetchWithRetry<T>(
  url: string,
  init: RequestInit,
  opts: RetryOptions & { read: (res: Response) => Promise<T> },
): Promise<T> {
  const { retries = 2, baseDelayMs = 1000, signal, read } = opts;
  let attempt = 0;
  for (;;) {
    if (signal?.aborted) throw abortError();
    const timeoutMs = opts.timeouts?.length
      ? opts.timeouts[Math.min(attempt, opts.timeouts.length - 1)]
      : (opts.timeoutMs ?? 30_000);
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    try {
      const res = await fetch(url, { ...init, signal: controller.signal });
      if (!res.ok) {
        void res.body?.cancel().catch(() => {});
        throw new HttpError(res.status, url, parseRetryAfter(res.headers.get('retry-after')));
      }
      // O corpo é lido ainda sob o mesmo controller/tempo-limite.
      const body = await abortable(read(res), controller.signal);
      return body;
    } catch (e) {
      if (signal?.aborted) throw abortError();
      const err = timedOut ? new TimeoutError(url, timeoutMs) : e;
      if (attempt >= retries || !isRetryable(err)) throw err;
      attempt++;
      await sleep(baseDelayMs * 2 ** (attempt - 1), signal);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    }
  }
}

/**
 * Rejeita com AbortError assim que o sinal for acionado, mesmo que a promessa
 * (ex.: leitura de um corpo que não respeita o sinal) nunca termine.
 */
function abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}
